<template>
	<SectionCard title="Stima" icon="mdi-chart-bell-curve-cumulative" :subtitle="sottotitolo">
		<div v-if="loading" class="sr-empty"><v-progress-circular indeterminate size="28" color="primary" /></div>
		<div v-else-if="!result" class="sr-empty">Scegli leva e prodotti: la stima si aggiorna da sola.</div>
		<div v-else-if="result.empty" class="sr-empty">Nessun prodotto in questo ambito.</div>

		<!-- spedizione gratis -->
		<template v-else-if="result.lever === 'shipping'">
			<div class="sr-sentence">
				In {{ result.days }} giorni ti aspetti circa <b>{{ result.orders }}</b> ordini sul sito: <b>{{ result.above }}</b> sono già sopra {{ euro(result.threshold) }} e
				<b>{{ result.near }}</b> ci sono vicini (dal 70% della soglia in su).
			</div>
			<table class="sr-table mt-3">
				<thead>
					<tr>
						<th></th>
						<th v-for="s in SCENARI" :key="s.key">{{ s.label }}</th>
					</tr>
				</thead>
				<tbody>
					<tr>
						<td>Ordini che salgono sopra soglia</td>
						<td v-for="s in SCENARI" :key="s.key">{{ numero(result[s.key].orders) }}</td>
					</tr>
					<tr>
						<td>Effetto sul fatturato</td>
						<td v-for="s in SCENARI" :key="s.key" :class="segno(result[s.key].revenue)">{{ euroSegno(result[s.key].revenue) }}</td>
					</tr>
				</tbody>
			</table>
			<div class="sr-muted mt-3">
				Le spese che oggi pagano i clienti sopra soglia ({{ euro(result.lostShipping) }}) diventano a tuo carico. Scenari: il 10%, 25% o 40% degli ordini vicini aggiunge prodotti per arrivare
				alla soglia.
			</div>
		</template>

		<!-- sconto / prezzo -->
		<template v-else>
			<div class="sr-chips">
				<v-chip v-if="result.elasticity.source === 'history'" small label color="green" text-color="white">
					<v-icon x-small left>mdi-history</v-icon>Dal tuo storico: {{ result.elasticity.items === 1 ? '1 prodotto' : result.elasticity.items + ' prodotti' }} già scontati in passato, {{ result.elasticity.promoWeeks }} settimane in promo
					(sconto medio {{ percento(result.elasticity.avgDiscount) }})
				</v-chip>
				<v-chip v-else small label outlined color="orange darken-2"><v-icon x-small left>mdi-alert-outline</v-icon>Stima basata su ipotesi: nessuna promo passata da confrontare</v-chip>
				<v-chip small label outlined>{{ result.discount >= 0 ? 'Sconto' : 'Aumento' }} {{ percento(Math.abs(result.discount)) }}</v-chip>
				<v-chip small label outlined>{{ result.selling }} di {{ result.items }} prodotti venduti sul sito negli ultimi 90 giorni</v-chip>
			</div>

			<table class="sr-table mt-3">
				<thead>
					<tr>
						<th></th>
						<th>Senza promo</th>
						<th v-for="s in SCENARI" :key="s.key">{{ s.label }}</th>
					</tr>
				</thead>
				<tbody>
					<tr>
						<td>Pezzi</td>
						<td>{{ numero(result.base.units) }}</td>
						<td v-for="s in SCENARI" :key="s.key">{{ numero(result[s.key].units) }}</td>
					</tr>
					<tr>
						<td>Fatturato</td>
						<td>{{ euro(result.base.revenue) }}</td>
						<td v-for="s in SCENARI" :key="s.key" :class="segno(result[s.key].revenue - result.base.revenue)">{{ euro(result[s.key].revenue) }}</td>
					</tr>
					<tr>
						<td>Margine</td>
						<td>{{ result.base.margin === null ? '—' : euro(result.base.margin) }}</td>
						<td v-for="s in SCENARI" :key="s.key" :class="result.base.margin === null ? '' : segno(result[s.key].margin - result.base.margin)">
							{{ result[s.key].margin === null ? '—' : euro(result[s.key].margin) }}
						</td>
					</tr>
				</tbody>
			</table>

			<div class="sr-sentence mt-3">
				<template v-if="result.breakEven && result.breakEven.possible">
					Per non perdere margine ti servono almeno il <b>{{ result.breakEven.uplift }}%</b> di pezzi in più.
					<span v-if="pezziInPiu(result.mid) >= result.breakEven.uplift" class="green--text text--darken-2">Lo scenario probabile ci arriva (+{{ pezziInPiu(result.mid) }}%).</span>
					<span v-else class="orange--text text--darken-3">Lo scenario probabile si ferma a +{{ pezziInPiu(result.mid) }}%: guadagni clienti, non margine.</span>
				</template>
				<template v-else-if="result.breakEven && !result.breakEven.possible">
					<span class="error--text">Con questo sconto vendi sotto costo: ogni pezzo in più è margine perso.</span>
				</template>
				<template v-else>Margine non calcolabile: i prodotti venduti non hanno un costo d'acquisto affidabile.</template>
			</div>
			<div v-if="copertura" class="sr-muted mt-1">{{ copertura }}</div>

			<div v-if="result.base.units === 0" class="sr-muted mt-2">Questi prodotti non hanno venduto sul sito negli ultimi 90 giorni: senza una base la stima è zero.</div>
			<div v-if="result.capped" class="sr-muted mt-2">Ambito molto grande: la stima considera i primi 3000 prodotti.</div>

			<div v-if="result.top && result.top.length" class="mt-4">
				<div class="sr-label">Prodotti che pesano di più</div>
				<div v-for="p in result.top" :key="p.id" class="sr-top">
					<span class="sr-top-name">{{ p.title || p.sku }}</span>
					<span class="sr-muted">{{ euroCent(p.price) }} · {{ String(p.perDay).replace('.', ',') }} pz/giorno</span>
				</div>
			</div>
		</template>
	</SectionCard>
</template>

<script>
import SectionCard from '@/components/core/SectionCard.vue';
import { euro, euroCent, percento } from '../formato';

const SCENARI = [
	{ key: 'low', label: 'Prudente' },
	{ key: 'mid', label: 'Probabile' },
	{ key: 'high', label: 'Ottimista' },
];

// Risultato della simulazione: oggi contro tre scenari, piu' il pareggio sul margine
export default {
	name: 'SimulatorResult',
	components: { SectionCard },
	props: {
		result: { type: Object, default: null },
		loading: { type: Boolean, default: false },
	},
	data() {
		return { SCENARI };
	},
	computed: {
		// margine parziale: lo si dice, invece di nasconderlo
		copertura() {
			const c = this.result && this.result.marginCoverage;
			if (!c || !c.withCost || c.withCost >= c.selling) return '';
			return `Margine calcolato su ${c.withCost} dei ${c.selling} prodotti venduti: agli altri manca il costo d'acquisto o è da verificare.`;
		},
		sottotitolo() {
			return this.result && this.result.days ? `prossimi ${this.result.days} giorni, solo sito` : '';
		},
	},
	methods: {
		euro,
		euroCent,
		percento,
		numero(v) {
			return v === null || v === undefined ? '—' : String(Math.round(v * 10) / 10).replace('.', ',');
		},
		euroSegno(v) {
			return `${v > 0 ? '+' : ''}${euro(v)}`;
		},
		segno(delta) {
			if (!delta) return '';
			return delta > 0 ? 'green--text text--darken-2' : 'error--text';
		},
		pezziInPiu(s) {
			const b = this.result.base.units;
			return b ? Math.round((s.units / b - 1) * 100) : 0;
		},
	},
};
</script>

<style scoped>
.sr-empty {
	text-align: center;
	padding: 32px 0;
	color: #5f6b7a;
}
.sr-chips {
	display: flex;
	flex-wrap: wrap;
	gap: 6px;
}
.sr-table {
	width: 100%;
	border-collapse: collapse;
	font-size: 13px;
}
.sr-table th {
	font-size: 12px;
	font-weight: 600;
	color: #5f6b7a;
	text-align: right;
	padding: 6px 8px;
	border-bottom: 1px solid #e3e8ef;
}
.sr-table td {
	text-align: right;
	padding: 8px;
	border-bottom: 1px solid #f0f2f5;
	white-space: nowrap;
}
.sr-table th:first-child,
.sr-table td:first-child {
	text-align: left;
	color: #1f2d3d;
}
.sr-sentence {
	font-size: 13px;
	color: #1f2d3d;
}
.sr-muted {
	font-size: 12px;
	color: #5f6b7a;
}
.sr-label {
	font-size: 12px;
	font-weight: 600;
	color: #5f6b7a;
	margin-bottom: 4px;
}
.sr-top {
	display: flex;
	justify-content: space-between;
	gap: 12px;
	padding: 4px 0;
	border-bottom: 1px solid #f0f2f5;
	font-size: 13px;
}
.sr-top-name {
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}
</style>
